import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, DatePicker, Input, InputNumber, Select, Spin, Table, Tag } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { previewBillingPolicyApi } from "../../../../../services/billingPolicies.services";
import {
  MODE_LABELS,
  RULE_LABELS,
  apiError,
  configurationErrors,
  facilityTime,
  formatAmount,
} from "./policyUtils";
import styles from "./BillingPolicies.module.css";

let stayKey = 0;
const stay = (kind, label, start, end, rate) => ({ key: ++stayKey, kind, label, start, end, rate });

// Sample stays anchored two days back so they read like recent admissions.
const buildPreset = (name) => {
  const d = dayjs().startOf("day").subtract(2, "day");
  switch (name) {
    case "overnight":
      return [stay("bed", "General A1", d.hour(22), d.add(2, "day").hour(8), 1000)];
    case "long":
      return [stay("bed", "General A1", d.hour(10), d.hour(10).add(50 * 60 + 30, "minute"), 1000)];
    case "sameDay":
      return [stay("bed", "Day care", d.hour(9), d.hour(14), 1000)];
    case "transfer":
      return [
        stay("bed", "General A1", d.hour(20), d.add(1, "day").hour(10), 1000),
        stay("bed", "ICU 3", d.add(1, "day").hour(12), d.add(2, "day").hour(9), 3000),
      ];
    case "package":
      return [
        stay("bed", "General A1", d.hour(22), d.add(2, "day").hour(8), 1000),
        stay("package", "Rehab package", d.add(1, "day").hour(9), d.add(2, "day").hour(8), 500),
      ];
    default:
      return [];
  }
};

const PRESETS = [
  { key: "overnight", label: "22:00 → 08:00, 2 nights" },
  { key: "long", label: "50.5 hours" },
  { key: "sameDay", label: "Same day, 5h" },
  { key: "transfer", label: "Bed transfer" },
  { key: "package", label: "Bed + package" },
];

const toPayloadStays = (stays) =>
  stays.map(({ kind, label, start, end, rate }) => ({
    kind,
    label,
    start: start?.format(),
    end: end?.format(),
    rate: rate || 0,
  }));

const PERIOD_COLUMNS = [
  { title: "Stay", dataIndex: "stay", width: 140, ellipsis: true },
  {
    title: "Period",
    key: "period",
    render: (_, p) => (
      <span>
        {p.date ? <b>{dayjs(p.date).format("DD MMM")}</b> : null}
        <span className={styles.muted}>
          {p.date ? "  " : ""}
          {facilityTime(p.start)} → {facilityTime(p.end)}
        </span>
      </span>
    ),
  },
  { title: "Hours", dataIndex: "occupied_hours", width: 70, align: "right" },
  {
    title: "Rule",
    dataIndex: "rule",
    width: 100,
    render: (rule) => <Tag>{RULE_LABELS[rule] || rule}</Tag>,
  },
  {
    title: "Charged",
    dataIndex: "fraction",
    width: 80,
    align: "right",
    render: (f) => (f === 1 ? "1 day" : f === 0.5 ? "½ day" : <span className={styles.muted}>free</span>),
  },
  { title: "Rate", dataIndex: "rate", width: 90, align: "right", render: formatAmount },
  { title: "Amount", dataIndex: "amount", width: 100, align: "right", render: formatAmount },
];

const PreviewPanel = ({ facilityId, billingMode, configuration, compareWith }) => {
  const [stays, setStays] = useState(() => buildPreset("overnight"));
  const [preset, setPreset] = useState("overnight");
  const [result, setResult] = useState(null);
  const [compare, setCompare] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  const invalidConfig = configurationErrors(billingMode, configuration);
  const showCompare =
    compareWith &&
    (compareWith.billing_mode !== billingMode ||
      JSON.stringify(compareWith.configuration) !== JSON.stringify(configuration));
  const payloadStays = useMemo(() => toPayloadStays(stays), [stays]);

  useEffect(() => {
    if (!facilityId || !billingMode || invalidConfig) return undefined;
    const incomplete = stays.some((s) => !s.start || !s.end);
    if (incomplete || !stays.length) return undefined;

    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const base = { facility_id: facilityId, stays: payloadStays };
        const [draft, current] = await Promise.all([
          previewBillingPolicyApi({ ...base, billing_mode: billingMode, configuration }),
          showCompare
            ? previewBillingPolicyApi({
                ...base,
                billing_mode: compareWith.billing_mode,
                configuration: compareWith.configuration,
              })
            : Promise.resolve(null),
        ]);
        if (id !== requestId.current) return;
        setResult(draft?.data?.data || null);
        setCompare(current?.data?.data || null);
        setError(null);
      } catch (e) {
        if (id !== requestId.current) return;
        setError(apiError(e, "Couldn't calculate the preview"));
        setResult(null);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, billingMode, JSON.stringify(configuration), payloadStays, showCompare]);

  const updateStay = (key, patch) => {
    setPreset(null);
    setStays((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  const applyPreset = (key) => {
    setPreset(key);
    setStays(buildPreset(key));
  };

  const difference = compare && result ? result.total_amount - compare.total_amount : null;

  return (
    <div className={styles.preview}>
      <div className={styles.sectionTitle}>Test with sample stays</div>
      <div className={styles.presets}>
        {PRESETS.map((p) => (
          <Button
            key={p.key}
            size="small"
            type={preset === p.key ? "primary" : "default"}
            onClick={() => applyPreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <div className={styles.stayList}>
        {stays.map((s) => (
          <div key={s.key} className={styles.stayRow}>
            <Select
              value={s.kind}
              onChange={(kind) => updateStay(s.key, { kind })}
              options={[
                { value: "bed", label: "Bed" },
                { value: "package", label: "Package" },
              ]}
              style={{ width: 100 }}
            />
            <Input
              value={s.label}
              onChange={(e) => updateStay(s.key, { label: e.target.value })}
              placeholder="Label"
              style={{ width: 130 }}
            />
            <DatePicker
              showTime={{ format: "HH:mm", minuteStep: 5 }}
              format="DD MMM, HH:mm"
              value={s.start}
              onChange={(start) => updateStay(s.key, { start })}
              placeholder="Start"
              allowClear={false}
            />
            <span className={styles.muted}>→</span>
            <DatePicker
              showTime={{ format: "HH:mm", minuteStep: 5 }}
              format="DD MMM, HH:mm"
              value={s.end}
              onChange={(end) => updateStay(s.key, { end })}
              placeholder="End"
              allowClear={false}
            />
            <InputNumber
              value={s.rate}
              min={0}
              prefix="₹"
              onChange={(rate) => updateStay(s.key, { rate })}
              style={{ width: 110 }}
            />
            <Button
              type="text"
              icon={<DeleteOutlined />}
              aria-label="Remove stay"
              disabled={stays.length === 1}
              onClick={() => {
                setPreset(null);
                setStays((prev) => prev.filter((x) => x.key !== s.key));
              }}
            />
          </div>
        ))}
        {stays.length < 10 && (
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ paddingLeft: 0 }}
            onClick={() => {
              const last = stays[stays.length - 1];
              setPreset(null);
              setStays((prev) => [
                ...prev,
                stay("bed", `Bed ${prev.length + 1}`, last?.end, last?.end?.add(1, "day"), 1000),
              ]);
            }}
          >
            Add stay
          </Button>
        )}
        <div className={styles.hint}>
          Beds are treated as one stay with transfers; each package is priced on its own. Times are
          in the facility's timezone.
        </div>
      </div>

      {invalidConfig ? (
        <Alert type="warning" showIcon message="Fix the highlighted rules to see the preview." />
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : (
        <Spin spinning={loading}>
          {result && (
            <>
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Billable days</div>
                  <div className={styles.statValue}>{result.billable_days}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Total</div>
                  <div className={styles.statValue}>{formatAmount(result.total_amount)}</div>
                </div>
                {compare && (
                  <div className={styles.stat}>
                    <div className={styles.statLabel}>
                      vs current ({MODE_LABELS[compare.billing_mode]})
                    </div>
                    <div className={styles.statValue}>
                      {formatAmount(compare.total_amount)}
                      <span
                        className={
                          difference > 0 ? styles.up : difference < 0 ? styles.down : styles.muted
                        }
                      >
                        {" "}
                        {difference === 0
                          ? "same"
                          : `${difference > 0 ? "+" : "−"}${formatAmount(Math.abs(difference))}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {result.stays.length > 1 && (
                <div className={styles.stayTotals}>
                  {result.stays.map((s) => (
                    <span key={`${s.kind}-${s.label}`}>
                      {s.label}: <b>{s.billable_days}</b> days · {formatAmount(s.amount)}
                    </span>
                  ))}
                </div>
              )}
              <Table
                size="small"
                rowKey={(p, i) => `${p.stay}-${p.start}-${i}`}
                columns={PERIOD_COLUMNS}
                dataSource={result.periods}
                pagination={false}
                scroll={{ x: 720 }}
              />
            </>
          )}
        </Spin>
      )}
    </div>
  );
};

export default PreviewPanel;
